import React from "react";
import { Box, Option } from "@mui/joy";
import { SCreate } from "../../styles/StyledMUI";
import { textTokens } from "../../styles/globalStyles";
import DarkSelect from "../common/DarkSelect";
import DarkTypography from "../common/DarkTypography";
import IconButtonCTA from "../common/IconButtonCTA";
import Pagination from "../common/Pagination";
import { Permission, usePermissions } from "../../contexts/app/permissions";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import useEvents from "./hooks/useEvents";
import EventCard from "./display/EventCard";
import EventFormModal from "./display/EventFormModal";
import EventParticipantsModal from "./display/EventParticipantsModal";
import { eventLifeStatus } from "./eventStatusHelpers";
import { DEFAULT_PAGINATION_LIMIT } from "../../utils/paginationHelpers";
import { useEvent } from "../../contexts/api";
import type { Event, CreateEventRequest, UpdateEventRequest } from "../../api/events/Event.typed";

const STATUS_FILTER_ALL = "__all";

export default function EventsPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.EVENTS_EDIT);
  const { showConfirmation } = useConfirmationModal();
  const { updateEvent } = useEvent();

  const { events, loading, refresh, create, update, remove, toggleOnline, uploadBackground } =
    useEvents();

  const [statusFilter, setStatusFilter] = React.useState<string>(STATUS_FILTER_ALL);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Event | null>(null);
  const [viewingParticipants, setViewingParticipants] = React.useState<Event | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(DEFAULT_PAGINATION_LIMIT);

  const filtered = React.useMemo(() => {
    if (statusFilter === STATUS_FILTER_ALL) return events;

    return events.filter((e) => eventLifeStatus(e) === statusFilter);
  }, [events, statusFilter]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageEvents = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;

    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const handleCreate = () => setCreateOpen(true);

  const handleSubmitCreate = async (
    req: CreateEventRequest | UpdateEventRequest,
  ): Promise<Event | null | undefined> => {
    const created = await create(req as CreateEventRequest);
    if (created) setCreateOpen(false);

    return created ?? null;
  };

  const handleSubmitEdit = async (
    req: CreateEventRequest | UpdateEventRequest,
  ): Promise<Event | null | undefined> => {
    const updated = await update(req as UpdateEventRequest);

    if (updated) {
      setEditing(null);
      if (viewingParticipants?.id === updated.id) setViewingParticipants(updated);
    }

    return updated ?? null;
  };

  const handleDelete = async (event: Event) => {
    const ok = await showConfirmation(
      "Supprimer l'événement",
      `Supprimer « ${event.name} » ? Les participants seront également supprimés.`,
    );
    if (ok) await remove(event.id);
  };

  const handleUpdateEventForParticipantsModal = async (req: UpdateEventRequest) => {
    const updated = await updateEvent(req);
    if (updated) setViewingParticipants(updated);
  };

  const handleParticipantsClose = async () => {
    await refresh();
    setViewingParticipants(null);
  };

  const showEmpty = !loading && pageEvents.length === 0;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <DarkSelect
            size="sm"
            value={statusFilter}
            onChange={(_, v) => v != null && setStatusFilter(v as string)}
            placeholder="Statut"
            aria-label="Filtrer par statut"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              maxWidth: { sm: 360 },
              flex: { sm: "0 1 auto" },
            }}
          >
            <Option value={STATUS_FILTER_ALL}>Tous statuts</Option>
            <Option value="upcoming">À venir</Option>
            <Option value="ongoing">En cours</Option>
            <Option value="past">Terminé</Option>
          </DarkSelect>

          {canEdit && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
              <IconButtonCTA
                aria-label="Nouvel événement"
                ctaVariant="accent"
                variant="plain"
                onClick={handleCreate}
              >
                <SCreate />
              </IconButtonCTA>
            </Box>
          )}
        </Box>

        {showEmpty && (
          <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
            Aucun événement.
          </DarkTypography>
        )}

        {pageEvents.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            canEdit={canEdit}
            onEdit={() => setEditing(event)}
            onParticipants={() => setViewingParticipants(event)}
            onDelete={() => handleDelete(event)}
            onToggleOnline={canEdit ? (online) => toggleOnline(event.id, online) : undefined}
          />
        ))}
      </Box>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
          }}
          loading={loading}
        />
      )}

      <EventFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleSubmitCreate}
        onUploadBackground={canEdit ? uploadBackground : undefined}
      />

      <EventFormModal
        open={editing != null}
        onClose={() => setEditing(null)}
        event={editing}
        onSubmit={handleSubmitEdit}
        onUploadBackground={canEdit ? uploadBackground : undefined}
      />

      {viewingParticipants != null && (
        <EventParticipantsModal
          open
          onClose={handleParticipantsClose}
          event={viewingParticipants}
          canEdit={canEdit}
          onUpdateEvent={handleUpdateEventForParticipantsModal}
          onRefresh={refresh}
        />
      )}
    </Box>
  );
}
